import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as InactiveIcon,
  CalendarToday as CalendarIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { esES } from '@mui/x-data-grid/locales';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { schoolYearService } from '../../services/schoolYearService';
//import { format } from 'date-fns';
//import { es } from 'date-fns/locale';

interface SchoolYear {
  id: number;
  name: string;
  start_month: number;
  start_year: number;
  end_month: number;
  end_year: number;
  is_active: boolean;
  description: string | null;
  stats?: {
    total_income: number;
    total_expenses: number;
    balance: number;
    total_months: number;
    total_students: number;
  };
}

const SchoolYearList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<SchoolYear | null>(null);

  useEffect(() => {
    loadSchoolYears();
  }, [paginationModel]);

  const loadSchoolYears = async () => {
    try {
      setLoading(true);
      
      const params: any = {
        page: paginationModel.page + 1,
        per_page: paginationModel.pageSize
      };
      
      if (search) params.search = search;

      const response = await schoolYearService.getAll(params);
      
      setSchoolYears(response.data.data);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar cursos escolares');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await schoolYearService.activate(id);
      loadSchoolYears();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al activar curso');
    }
  };

  const handleDeactivate = async (id: number) => {
    try {
      await schoolYearService.deactivate(id);
      loadSchoolYears();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al desactivar curso');
    }
  };

  const handleDelete = async () => {
    if (!selectedYear) return;
    
    try {
      await schoolYearService.delete(selectedYear.id);
      setDeleteDialogOpen(false);
      setSelectedYear(null);
      loadSchoolYears();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al eliminar curso');
    }
  };

  const getMonthName = (monthNumber: number) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthNumber - 1];
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'name',
      headerName: 'Curso',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: 'period',
      headerName: 'Período',
      width: 250,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <CalendarIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2">
            {getMonthName(params.row.start_month)} {params.row.start_year} - {getMonthName(params.row.end_month)} {params.row.end_year}
          </Typography>
        </Box>
      )
    },
    {
      field: 'stats',
      headerName: 'Balance',
      width: 150,
      renderCell: (params) => {
        const balance = params.row.stats?.balance || 0;
        return (
          <Chip
            icon={<TrendingUpIcon />}
            label={`₽${balance.toLocaleString()}`}
            color={balance >= 0 ? 'success' : 'error'}
            size="small"
          />
        );
      }
    },
    {
      field: 'is_active',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        params.value ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="Activo"
            color="success"
            size="small"
          />
        ) : (
          <Chip
            icon={<InactiveIcon />}
            label="Inactivo"
            variant="outlined"
            size="small"
          />
        )
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 200,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Ver detalles">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/school-years/${params.row.id}`);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/school-years/${params.row.id}/edit`);
              }}
              disabled={!['admin'].includes(user?.role || '')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!params.row.is_active ? (
            <Tooltip title="Activar">
              <IconButton
                size="small"
                color="success"
                onClick={(e) => {
                  e.stopPropagation();
                  handleActivate(params.row.id);
                }}
                disabled={!['admin'].includes(user?.role || '')}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Desactivar">
              <IconButton
                size="small"
                color="warning"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeactivate(params.row.id);
                }}
                disabled={!['admin'].includes(user?.role || '')}
              >
                <InactiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Eliminar">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedYear(params.row);
                setDeleteDialogOpen(true);
              }}
              disabled={!['admin', 'manager'].includes(user?.role || '')}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Cursos Escolares
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/school-years/new')}
          disabled={!['admin'].includes(user?.role || '')}
        >
          Nuevo Curso
        </Button>
      </Box>

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              placeholder="Buscar curso por nombre..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadSchoolYears()}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              variant="contained"
              onClick={() => loadSchoolYears()}
              fullWidth
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabla */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={schoolYears}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={total}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowClick={(params) => navigate(`/school-years/${params.id}`)}
          disableRowSelectionOnClick
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
        />
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            ¿Está seguro de eliminar el curso escolar{' '}
            <strong>{selectedYear?.name}</strong>?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Esta acción no se puede deshacer. Si el curso tiene pagos asociados, no podrá eliminarse.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolYearList;